import type { PartnerOrganization } from "@entities";
import { AdvancedMarker, APIProvider, Pin } from "@vis.gl/react-google-maps";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { partnerOrganizationRepo } from "@repositories";
import { hasValidCoordinates } from "@shared/helpers/geo";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import type { EntityFilter } from "remult";
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LANGUAGE,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_REGION,
} from "../../../constants/google-maps-browser";
import GenericMap from "../../../components/mission-map/GenericMap";
import { DEFAULT_CENTER } from "../../mission-map/constants";

const MAP_ORGS_LIMIT = 2000;

interface PartnerOrganizationsMapProps {
  filterWhere: EntityFilter<PartnerOrganization>;
  canEdit: boolean;
  onEdit: (organization: PartnerOrganization) => void;
  onOpenOrganization?: (organization: PartnerOrganization) => void;
}

type MapPin = {
  key: string;
  org: PartnerOrganization;
  address: string;
  coordinates: { lat: number; lng: number };
  label?: string;
};

export function PartnerOrganizationsMap({
  filterWhere,
  canEdit,
  onEdit,
  onOpenOrganization,
}: PartnerOrganizationsMapProps) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const { data: organizations = [], isLoading } = useQuery({
    queryKey: ["partner-organizations-map", JSON.stringify(filterWhere)],
    queryFn: () =>
      partnerOrganizationRepo.find({
        where: filterWhere,
        orderBy: { name: "asc" },
        limit: MAP_ORGS_LIMIT,
        include: { addresses: true },
      }),
    staleTime: 30_000,
  });

  const pins = useMemo(() => {
    const result: MapPin[] = [];
    for (const org of organizations) {
      const rows = [...(org.addresses ?? [])].sort(
        (a, b) => a.sortOrder - b.sortOrder,
      );
      if (rows.length > 0) {
        rows.forEach((row, index) => {
          if (!hasValidCoordinates(row.coordinates)) return;
          result.push({
            key: `${org.id}-${row.id}`,
            org,
            address: row.address,
            coordinates: row.coordinates,
            label: row.label || (index === 0 ? "כתובת ראשית" : undefined),
          });
        });
        continue;
      }
      if (hasValidCoordinates(org.coordinates)) {
        result.push({
          key: org.id,
          org,
          address: org.address || "",
          coordinates: org.coordinates,
        });
      }
    }
    return result;
  }, [organizations]);

  const missingLocationCount = organizations.length - new Set(pins.map((pin) => pin.org.id)).size;
  const selected = pins.find((pin) => pin.key === selectedKey) ?? null;

  if (isLoading) {
    return (
      <Stack
        width="100%"
        height="70vh"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Stack>
    );
  }

  if (pins.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        אין ארגונים שותפים עם מיקום במפה לפי הסינון הנוכחי
      </Alert>
    );
  }

  return (
    <APIProvider
      apiKey={GOOGLE_MAPS_API_KEY}
      libraries={GOOGLE_MAPS_LIBRARIES}
      language={GOOGLE_MAPS_LANGUAGE}
      region={GOOGLE_MAPS_REGION}
    >
      <Box sx={{ position: "relative", width: "100%", height: "70vh" }}>
        {missingLocationCount > 0 ? (
          <Alert severity="info" sx={{ position: "absolute", top: 8, insetInlineEnd: 8, zIndex: 3, maxWidth: 320 }}>
            {missingLocationCount} ארגונים בלי מיקום במפה
          </Alert>
        ) : null}
        <GenericMap
          defaultCenter={DEFAULT_CENTER}
          defaultZoom={8}
          style={{ width: "100%", height: "100%" }}
        >
          {pins.map((pin) => (
            <AdvancedMarker
              key={pin.key}
              position={pin.coordinates}
              onClick={() => setSelectedKey(pin.key)}
              title={pin.org.name}
            >
              <Pin
                background={selectedKey === pin.key ? "#05204C" : "#00b4d8"}
                borderColor="#fff"
                glyphColor="#fff"
              />
            </AdvancedMarker>
          ))}
        </GenericMap>

        {selected && (
          <Paper
            elevation={3}
            sx={{
              position: "absolute",
              top: 16,
              insetInlineStart: 16,
              p: 2,
              maxWidth: 320,
              zIndex: 2,
              borderRadius: "12px",
            }}
          >
            <Stack gap={1}>
              <Typography variant="subtitle1" fontWeight={700}>
                {selected.org.name}
              </Typography>
              {selected.label ? (
                <Typography variant="caption" color="text.secondary">
                  {selected.label}
                </Typography>
              ) : null}
              <Typography variant="body2" color="text.secondary">
                {selected.address || "ללא כתובת"}
              </Typography>
              <Stack direction="row" gap={1} flexWrap="wrap">
                {onOpenOrganization ? (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => onOpenOrganization(selected.org)}
                  >
                    פרופיל
                  </Button>
                ) : null}
                {canEdit && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={async () => {
                      const full = await partnerOrganizationRepo.findId(
                        selected.org.id,
                        { include: { addresses: true } },
                      );
                      if (full) onEdit(full);
                    }}
                  >
                    עריכה
                  </Button>
                )}
                <Button size="small" onClick={() => setSelectedKey(null)}>
                  סגור
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}
      </Box>
    </APIProvider>
  );
}
